import React from 'react';
import { ChevronRight, Bell, Shield, CreditCard, HelpCircle, LogOut, Moon, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePi } from '../../contexts/PiContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import BottomNavigation from '../../components/Layout/BottomNavigation';
import Card from '../../components/UI/Card';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user, piBalance, isAuthenticated } = usePi();
  const { currentLanguage } = useLanguage();
  const { isDarkMode, toggleDarkMode } = useTheme();

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    const confirmLogout = window.confirm('Are you sure you want to logout?');
    if (confirmLogout) {
      signOut();
      navigate('/login');
    }
  };

  const settingsGroups = [
    {
      title: 'Account',
      items: [
        { icon: Shield, label: 'Privacy & Security', action: () => navigate('/settings/privacy-security') },
        { icon: Bell, label: 'Notifications', action: () => navigate('/settings/notifications') },
        { icon: CreditCard, label: 'Payment Methods', action: () => navigate('/payment-methods') }
      ]
    },
    {
      title: 'Preferences',
      items: [
        { icon: Globe, label: 'Language', value: currentLanguage.name, action: () => navigate('/settings/language') },
        { 
          icon: Moon, 
          label: 'Dark Mode', 
          action: toggleDarkMode,
          toggle: true,
          toggleValue: isDarkMode
        }
      ]
    },
    {
      title: 'Support',
      items: [
        { icon: HelpCircle, label: 'Help & Support', action: () => navigate('/settings/help-support') },
        { icon: LogOut, label: 'Logout', action: handleLogout, danger: true }
      ]
    }
  ];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="p-4">
        <h1 className="text-2xl font-bold text-white mb-4">Settings</h1>

        {/* Pi Balance Card */}
        {user && profile && (
          <Card className="p-4 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-200">Pi Balance</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connected to Pi Network</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">π 0.00</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Available</p>
              </div>
            </div>
          </Card>
        )}

        {/* Current Language Display */}
        <Card className="p-4 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe size={20} className="text-purple-600 dark:text-purple-400" />
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-200">Current Language</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{currentLanguage.nativeName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{currentLanguage.flag}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{currentLanguage.name}</span>
            </div>
          </div>
        </Card>

        {/* Settings Groups */}
        <div className="space-y-4">
          {settingsGroups.map((group, groupIndex) => (
            <Card key={groupIndex} className="overflow-hidden">
              <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200">{group.title}</h3>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {group.items.map((item, itemIndex) => (
                  <button
                    key={itemIndex}
                    onClick={item.action}
                    className={`w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 ${
                      item.danger ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon size={20} />
                      <span>{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.value && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">{item.value}</span>
                      )}
                      {item.toggle ? (
                        <div 
                          className={`w-12 h-6 rounded-full transition-all duration-300 ${
                            item.toggleValue 
                              ? 'bg-gradient-to-r from-purple-600 to-pink-500 shadow-lg shadow-purple-200 dark:shadow-purple-900' 
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            item.action();
                          }}
                        >
                          <div
                            className={`w-5 h-5 bg-white rounded-full transition-all duration-300 mt-0.5 shadow-md ${
                              item.toggleValue 
                                ? 'translate-x-6 shadow-purple-200 dark:shadow-purple-900' 
                                : 'translate-x-0.5'
                            }`}
                          />
                        </div>
                      ) : (
                        <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          ))}
        </div>

        {/* App Info */}
        <div className="mt-8 text-center">
          <p className="text-white/70 text-sm">πPi Dating v1.0.0</p>
          <p className="text-white/50 text-xs mt-1">Pioneer Dating App</p>
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default SettingsPage;